import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar, Clock, Building2, MapPin, ExternalLink, FileText, Loader2, Shield, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import Logo from './ui/Logo';
import AdmissaoStepper from './rh/AdmissaoStepper';
import { formatCPF, unformatCPF } from '../lib/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MinhaAdmissaoPage = () => {
  const { token } = useParams();
  const [cpf, setCpf] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState(null);
  const [baixandoAso, setBaixandoAso] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    if (!cpf || !senha) {
      toast.error('Preencha o CPF e a senha');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admissao-portal/acessar/${token}`, {
        cpf: unformatCPF(cpf),
        senha,
      });
      setDados(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link inválido ou credenciais incorretas');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAso = async () => {
    try {
      setBaixandoAso(true);
      const response = await axios.get(`${API}/admissao-portal/${token}/aso`, {
        params: { cpf: unformatCPF(cpf), senha },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ASO_Admissional.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Erro ao baixar o ASO');
    } finally {
      setBaixandoAso(false);
    }
  };

  if (!dados) {
    return (
      <div className="min-h-screen w-full flex flex-col lg:flex-row brand-canvas relative overflow-hidden">
        <div className="brand-glow brand-float" style={{ top: '-6rem', left: '-4rem', width: '28rem', height: '28rem', background: '#2563EB' }} />
        <div className="brand-glow brand-float" style={{ bottom: '-8rem', left: '20%', width: '32rem', height: '32rem', background: '#00D4CB', animationDelay: '2s' }} />

        <div className="relative z-10 flex flex-1 flex-col justify-between p-8 sm:p-12 lg:p-16">
          <Logo variant="full" theme="dark" showTagline iconClassName="h-10 w-auto" wordClassName="text-2xl" />
          <div className="max-w-xl py-12 lg:py-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold text-white/80 backdrop-blur-sm">
              <Shield className="h-3.5 w-3.5 text-[#00D4CB]" />
              Portal de Admissão
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-tight text-white">
              Acompanhe sua
              <br />
              <span className="brand-text-gradient">admissão em tempo real.</span>
            </h1>
            <p className="mt-6 max-w-md text-base sm:text-lg text-white/60">
              Acesse com seu CPF e a senha enviada por e-mail para acompanhar documentos, exame admissional e integração.
            </p>
          </div>
          <p className="hidden lg:block text-xs text-white/40">
            &copy; {new Date().getFullYear()} GridProfile &middot; Intelig&ecirc;ncia para Gest&atilde;o de Pessoas
          </p>
        </div>

        <div className="relative z-10 w-full lg:w-[480px] xl:w-[520px] flex items-center justify-center p-6 sm:p-10 lg:p-12 bg-[#0B1220]/40 lg:bg-[#0B1220]/60 backdrop-blur-xl lg:border-l border-white/10">
          <div className="w-full max-w-sm">
            <h2 className="text-2xl font-bold text-white">Minha Admissão</h2>
            <p className="mt-1 text-sm text-white/50">Use o CPF e a senha recebidos por e-mail</p>
            <form onSubmit={handleAuth} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="cpf" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">CPF</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input id="cpf" value={cpf} onChange={e => setCpf(formatCPF(e.target.value.replace(/\D/g, '').slice(0, 11)))}
                    placeholder="000.000.000-00"
                    className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50" required />
                </div>
              </div>
              <div>
                <Label htmlFor="senha" className="text-[11px] font-semibold uppercase tracking-wider text-white/50">Senha de 6 dígitos</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input id="senha" type="password" value={senha} onChange={e => setSenha(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    className="h-12 pl-11 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#00D4CB] focus-visible:border-[#00D4CB]/50" required />
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-medium py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Validando...</> : <><Shield className="mr-2 h-4 w-4" />Acessar Portal</>}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const { exame_admissional: exame, documentos, etapa_atual: etapaAtual } = dados;

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo variant="full" theme="light" iconClassName="h-8 w-auto" wordClassName="text-lg" />
          <span className="text-sm text-gray-500">Olá, <strong className="text-gray-900">{dados.employee_nome}</strong></span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Minha Admissão</h1>
          <p className="text-sm text-gray-500">Acompanhe sua admissão e conclua todas as etapas.</p>
        </div>

        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-900">
              {documentos?.status === 'finalizado' ? 'Documentos admissionais concluídos!' : 'Envio de documentos em andamento'}
            </p>
            <p className="text-sm text-emerald-700">
              {etapaAtual === 1 && 'Envie os documentos solicitados pelo RH para avançar.'}
              {etapaAtual === 2 && exame?.status !== 'concluido' && 'Sua próxima etapa é o exame admissional.'}
              {etapaAtual === 2 && exame?.status === 'concluido' && 'Aguarde o RH finalizar sua admissão.'}
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <AdmissaoStepper currentStep={etapaAtual} />
          </CardContent>
        </Card>

        {(exame?.status === 'agendado' || exame?.status === 'concluido') && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Exame admissional agendado</CardTitle>
              <Badge className={exame.status === 'concluido' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}>
                {exame.status === 'concluido' ? 'Realizado' : 'Aguardando realização'}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Data</p>
                    <p className="text-sm font-medium text-gray-900">{exame.data}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Horário</p>
                    <p className="text-sm font-medium text-gray-900">{exame.horario}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Clínica</p>
                    <p className="text-sm font-medium text-gray-900">{exame.clinica_nome}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-gray-500">Endereço</p>
                    <p className="text-sm font-medium text-gray-900">{exame.endereco}</p>
                  </div>
                </div>
              </div>
              {exame.orientacoes && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">{exame.orientacoes}</div>
              )}
              {exame.mapa_link && (
                <Button variant="outline" size="sm" onClick={() => window.open(exame.mapa_link, '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" /> Abrir no mapa
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {(exame?.status === 'agendado' || exame?.status === 'concluido') && (
          <Card>
            <CardHeader>
              <CardTitle>ASO do exame admissional</CardTitle>
              <CardDescription>
                {exame.aso_disponivel ? 'Seu atestado já está disponível para download.' : 'O ASO será enviado pelo RH após a realização do exame.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {exame.aso_disponivel ? (
                <Button onClick={handleDownloadAso} disabled={baixandoAso}>
                  {baixandoAso ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                  Baixar ASO
                </Button>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
                  <FileText className="h-4 w-4" /> ASO ainda não disponível
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default MinhaAdmissaoPage;
